import moment from "moment-timezone";

type StoredUser = {
  tz?: string | null;
};

type UserDayBounds = {
  start: Date | null;
  end: Date | null;
};

let configuredTimeZone: string | null = null;
let warnedInvalidTimeZone: string | null = null;

const readStoredUser = (): StoredUser | null => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null") as StoredUser | null;
  } catch {
    return null;
  }
};

export const normalizeUserTimeZone = (
  timeZone: unknown,
): string | null => {
  if (typeof timeZone !== "string") return null;

  const normalized = timeZone.trim();
  if (!normalized) return null;
  if (moment.tz.zone(normalized)) return normalized;

  if (warnedInvalidTimeZone !== normalized) {
    warnedInvalidTimeZone = normalized;
    console.warn(
      `[timezone] Invalid user.tz "${normalized}", falling back to the browser timezone.`,
    );
  }
  return null;
};

export const getStoredUserTimeZone = () =>
  normalizeUserTimeZone(readStoredUser()?.tz);

export const configureUserTimeZone = (
  user: StoredUser | null = readStoredUser(),
): string | null => {
  let timeZone = normalizeUserTimeZone(user?.tz);
  // Fallback: delegation user objects from the API may omit the tz field.
  // Use the stored (logged-in) user's timezone as fallback to prevent
  // moment from reverting to the browser timezone, which would cause
  // date formatting inconsistencies in the grid after delegate switching.
  if (!timeZone) {
    timeZone = getStoredUserTimeZone();
  }
  configuredTimeZone = timeZone;
  moment.tz.setDefault(timeZone || undefined);
  return timeZone;
};

export const getUserTimeZone = () =>
  configuredTimeZone || getStoredUserTimeZone() || moment.tz.guess();

export const toUserTimeZone = (value: moment.MomentInput) =>
  moment(value).tz(getUserTimeZone());

export const formatInUserTimeZone = (
  value: moment.MomentInput,
  format: string,
) =>
  toUserTimeZone(value).format(format);

export const getUserCalendarDateKey = (value: unknown): string | null => {
  if (moment.isMoment(value)) {
    return value.isValid() ? value.format("YYYY-MM-DD") : null;
  }

  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }

  if (
    !(value instanceof Date) &&
    typeof value !== "string" &&
    typeof value !== "number"
  ) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return toUserTimeZone(date).format("YYYY-MM-DD");
};

export const getUserDayBounds = (value: unknown): UserDayBounds => {
  const dateKey = getUserCalendarDateKey(value);
  if (!dateKey) return { start: null, end: null };

  const startMoment = moment.tz(
    dateKey,
    "YYYY-MM-DD",
    true,
    getUserTimeZone(),
  );
  if (!startMoment.isValid()) return { start: null, end: null };

  return {
    start: startMoment.toDate(),
    end: startMoment.clone().add(1, "day").toDate(),
  };
};

export const getUserDayBoundsForInstant = (
  value: moment.MomentInput,
): UserDayBounds =>
  getUserDayBounds(toUserTimeZone(value).format("YYYY-MM-DD"));

export const toUserStartOfDayISOString = (value: unknown): string | null => {
  const { start } = getUserDayBounds(value);
  return start ? start.toISOString() : null;
};

export const toUserEndOfDayISOString = (value: unknown): string | null => {
  const { end } = getUserDayBounds(value);
  return end ? end.toISOString() : null;
};

export const createDateTimeInUserTimeZone = (
  dateValue: unknown,
  timeValue: moment.MomentInput = new Date(),
): Date => {
  const dateKey = getUserCalendarDateKey(dateValue);
  if (!dateKey) return new Date(NaN);

  const timeMatch =
    typeof timeValue === "string"
      ? timeValue.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/)
      : null;
  const timeKey = timeMatch
    ? `${timeMatch[1].padStart(2, "0")}:${timeMatch[2]}:${timeMatch[3] || "00"}.000`
    : toUserTimeZone(timeValue).format("HH:mm:ss.SSS");
  return moment
    .tz(
      `${dateKey} ${timeKey}`,
      "YYYY-MM-DD HH:mm:ss.SSS",
      true,
      getUserTimeZone(),
    )
    .toDate();
};

const getClockTimeKey = (value: unknown): string | null => {
  if (typeof value === "string") {
    const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return null;

    return `${match[1].padStart(2, "0")}:${match[2]}:${match[3] || "00"}.000`;
  }

  if (moment.isMoment(value)) {
    return [
      String(value.hour()).padStart(2, "0"),
      String(value.minute()).padStart(2, "0"),
      `${String(value.second()).padStart(2, "0")}.${String(value.millisecond()).padStart(3, "0")}`,
    ].join(":");
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return [
      String(value.getHours()).padStart(2, "0"),
      String(value.getMinutes()).padStart(2, "0"),
      `${String(value.getSeconds()).padStart(2, "0")}.${String(value.getMilliseconds()).padStart(3, "0")}`,
    ].join(":");
  }

  return null;
};

export const createDateTimeInUserTimeZoneFromClockTime = (
  dateValue: unknown,
  clockTimeValue: unknown,
): Date => {
  const dateKey = getUserCalendarDateKey(dateValue);
  const timeKey = getClockTimeKey(clockTimeValue);
  if (!dateKey || !timeKey) return new Date(NaN);

  return moment
    .tz(
      `${dateKey} ${timeKey}`,
      "YYYY-MM-DD HH:mm:ss.SSS",
      true,
      getUserTimeZone(),
    )
    .toDate();
};

export const toUserTimeZoneClockDate = (
  value: moment.MomentInput,
): Date | null => {
  const userMoment = toUserTimeZone(value);
  if (!userMoment.isValid()) return null;

  const clockDate = new Date();
  clockDate.setHours(
    userMoment.hours(),
    userMoment.minutes(),
    userMoment.seconds(),
    userMoment.milliseconds(),
  );

  return clockDate;
};

export const addDaysInUserTimeZone = (
  value: moment.MomentInput,
  days: number,
): Date | null => {
  const userMoment = toUserTimeZone(value);
  if (!userMoment.isValid()) return null;

  return userMoment.add(days, "day").toDate();
};

configureUserTimeZone();
